/**
 * Upload Utilities
 * 
 * Helper functions for formatting and calculating upload-related data
 */

/**
 * Formats upload speed in bytes per second to a human-readable string
 * 
 * @param bytesPerSecond - Upload speed in bytes per second
 * @returns Formatted speed string (e.g., "2.5 MB/s" or "512 KB/s")
 */
export function formatSpeed(bytesPerSecond: number): string {
  const mbps = bytesPerSecond / (1024 * 1024);
  
  if (mbps >= 1) {
    // Display in MB/s with one decimal place
    return `${mbps.toFixed(1)} MB/s`;
  }
  
  // Display in KB/s with no decimal places
  const kbps = bytesPerSecond / 1024;
  return `${Math.round(kbps)} KB/s`;
}

/**
 * Formats file size in bytes to a human-readable string
 * 
 * @param bytes - File size in bytes
 * @returns Formatted size string (e.g., "15.3 MB")
 */
export function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

/**
 * Rolling Average Calculator for smoothing upload speed measurements
 * 
 * Maintains a 3-second window of speed measurements and calculates
 * the average to smooth out instantaneous fluctuations.
 */
export class RollingAverageCalculator {
  private measurements: Array<{ timestamp: number; speed: number }> = [];
  private readonly windowMs: number;

  /**
   * Creates a new RollingAverageCalculator
   * 
   * @param windowSeconds - Time window in seconds for averaging (default: 3)
   */
  constructor(windowSeconds: number = 3) {
    this.windowMs = windowSeconds * 1000;
  }

  /**
   * Adds a new speed measurement
   * 
   * @param speed - Upload speed in bytes per second
   */
  addMeasurement(speed: number): void {
    const now = Date.now();
    this.measurements.push({ timestamp: now, speed });
    
    // Remove measurements outside the time window
    this.measurements = this.measurements.filter(
      m => now - m.timestamp <= this.windowMs
    );
  }

  /**
   * Calculates the rolling average speed
   * 
   * @returns Average speed in bytes per second
   */
  getAverage(): number {
    if (this.measurements.length === 0) {
      return 0;
    }
    
    const sum = this.measurements.reduce((acc, m) => acc + m.speed, 0);
    return sum / this.measurements.length;
  }

  /**
   * Resets all measurements
   */
  reset(): void {
    this.measurements = [];
  }
}

/**
 * Throttles a function to limit how often it can be called
 * 
 * @param func - Function to throttle
 * @param limitMs - Minimum time between calls in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limitMs: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  let lastResult: ReturnType<T>;

  return function(this: any, ...args: Parameters<T>): void {
    if (!inThrottle) {
      lastResult = func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limitMs);
    }
  };
}

/**
 * Generate pre-signed upload URL for direct R2 uploads
 * 
 * Calls the /api/generate-upload-url endpoint with authentication to get
 * a pre-signed URL for uploading files directly to Cloudflare R2.
 * 
 * @param params - Upload parameters
 * @param params.orderId - UUID of the order
 * @param params.fileExtension - File extension (jpg, png, tiff, etc.)
 * @param params.fileSize - File size in bytes
 * @returns Promise resolving to signed URL, object key, and public file URL
 * @throws Error if authentication fails (401) or request fails
 * 
 * @example
 * ```typescript
 * const { signedUploadUrl, objectKey, publicFileUrl } = await generateUploadUrl({
 *   orderId: 'a1b2c3d4-...',
 *   fileExtension: 'jpg',
 *   fileSize: 1024000
 * });
 * ```
 */
export async function generateUploadUrl(params: {
  orderId: string;
  fileExtension: string;
  fileSize: number;
}): Promise<{
  signedUploadUrl: string;
  objectKey: string;
  publicFileUrl: string;
}> {
  // Import Supabase client dynamically to avoid circular dependencies
  const { supabase } = await import('./supabase');
  
  // Get the current session to extract the auth token
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    throw new Error('Authentication failed. Please log in again.');
  }
  
  // Make POST request to generate-upload-url API with auth token
  const response = await fetch('/api/generate-upload-url', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      orderId: params.orderId,
      fileExtension: params.fileExtension,
      fileSize: params.fileSize,
    }),
  });
  
  // Handle authentication errors (401)
  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }
  
  // Handle other errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Failed to generate upload URL: ${response.statusText}`);
  }
  
  // Parse and return the response
  const data = await response.json();
  return {
    signedUploadUrl: data.signedUploadUrl,
    objectKey: data.objectKey,
    publicFileUrl: data.publicFileUrl,
  };
}

/**
 * Upload file directly to R2 using pre-signed URL
 * 
 * Performs a PUT request to the signed URL using XMLHttpRequest to enable
 * progress tracking. Sets the Content-Type header to match the file's MIME type.
 * 
 * @param params - Upload parameters
 * @param params.signedUploadUrl - Pre-signed URL from generateUploadUrl()
 * @param params.file - File object to upload
 * @param params.onProgress - Optional callback for progress updates (0-100)
 * @returns Promise that resolves when upload completes successfully
 * @throws Error if upload fails or returns non-200 status
 * 
 * @example
 * ```typescript
 * await uploadToR2({
 *   signedUploadUrl: 'https://...',
 *   file: fileObject,
 *   onProgress: (progress) => console.log(`${progress}% complete`)
 * });
 * ```
 */
export async function uploadToR2(params: {
  signedUploadUrl: string;
  file: File;
  onProgress?: (progress: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && params.onProgress) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        params.onProgress(percentage);
      }
    });
    
    // Handle successful upload
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    });
    
    // Handle network errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error occurred during upload'));
    });
    
    // Handle request abortion
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });
    
    // Open PUT request to signed URL
    xhr.open('PUT', params.signedUploadUrl);
    
    // Set Content-Type header to file MIME type
    xhr.setRequestHeader('Content-Type', params.file.type);
    
    // Send file binary data
    xhr.send(params.file);
  });
}

/**
 * Save upload metadata after successful R2 upload
 * 
 * Calls the /api/save-upload-metadata endpoint with authentication to save
 * file metadata to the database after a successful direct R2 upload.
 * 
 * @param params - Metadata parameters
 * @param params.objectKey - R2 object key from generateUploadUrl()
 * @param params.orderId - UUID of the order
 * @param params.slotIndex - Optional HDR slot index (0-2)
 * @param params.role - Optional image role ('raw' | 'edited' | 'reference')
 * @param params.imageId - Optional existing image ID for updates
 * @returns Promise resolving to success confirmation and imageId
 * @throws Error if authentication fails (401) or request fails
 * 
 * @example
 * ```typescript
 * const { success, imageId } = await saveUploadMetadata({
 *   objectKey: 'users/.../orders/.../uuid.jpg',
 *   orderId: 'a1b2c3d4-...',
 *   role: 'edited'
 * });
 * ```
 */
export async function saveUploadMetadata(params: {
  objectKey: string;
  orderId: string;
  slotIndex?: number;
  role?: 'raw' | 'edited' | 'reference';
  imageId?: string;
  parentImageId?: string;
  fileName?: string;
}): Promise<{ success: true; imageId: string }> {
  // Import Supabase client dynamically to avoid circular dependencies
  const { supabase } = await import('./supabase');
  
  // Get the current session to extract the auth token
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    throw new Error('Authentication failed. Please log in again.');
  }
  
  // Make POST request to save-upload-metadata API with auth token
  const response = await fetch('/api/save-upload-metadata', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      objectKey: params.objectKey,
      orderId: params.orderId,
      ...(params.slotIndex !== undefined && { slotIndex: params.slotIndex }),
      ...(params.role && { role: params.role }),
      ...(params.imageId && { imageId: params.imageId }),
      ...(params.parentImageId && { parentImageId: params.parentImageId }),
      ...(params.fileName && { fileName: params.fileName }),
    }),
  });
  
  // Handle authentication errors (401)
  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }
  
  // Handle other errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Failed to save upload metadata: ${response.statusText}`);
  }
  
  // Parse and return the response
  const data = await response.json();
  return {
    success: data.success,
    imageId: data.imageId,
  };
}

/**
 * Complete three-step upload flow with retry logic
 * 
 * Orchestrates the entire upload process:
 * 1. Generate pre-signed URL
 * 2. Upload file directly to R2
 * 3. Save upload metadata to database
 * 
 * Implements automatic retry logic with exponential backoff:
 * - Max 3 attempts (configurable via maxRetries parameter)
 * - Retries on: expired URL (403), network errors, R2 errors
 * - Exponential backoff: 0ms, 1000ms, 2000ms
 * - Requests new signed URL on each retry attempt
 * 
 * @param params - Upload parameters
 * @param params.file - File object to upload
 * @param params.orderId - UUID of the order
 * @param params.slotIndex - Optional HDR slot index (0-2)
 * @param params.role - Optional image role ('raw' | 'edited' | 'reference')
 * @param params.imageId - Optional existing image ID for updates
 * @param params.onProgress - Optional callback for progress updates (0-100)
 * @param params.maxRetries - Optional max retry attempts (default: 3)
 * @returns Promise resolving to success confirmation, imageId, and public file URL
 * @throws Error if all retry attempts fail or authentication fails
 * 
 * @example
 * ```typescript
 * const result = await uploadFileToR2({
 *   file: fileObject,
 *   orderId: 'a1b2c3d4-...',
 *   role: 'edited',
 *   onProgress: (progress) => console.log(`${progress}% complete`)
 * });
 * console.log(`Upload successful! Image ID: ${result.imageId}`);
 * ```
 */
export async function uploadFileToR2(params: {
  file: File;
  orderId: string;
  slotIndex?: number;
  role?: 'raw' | 'edited' | 'reference';
  imageId?: string;
  parentImageId?: string;
  onProgress?: (progress: number) => void;
  maxRetries?: number;
}): Promise<{ success: true; imageId: string; publicFileUrl: string }> {
  const maxRetries = params.maxRetries ?? 3;
  const fileExtension = params.file.name.split('.').pop() || '';
  const fileSize = params.file.size;
  
  let lastError: Error | null = null;
  
  // Retry loop with exponential backoff
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Step 1: Generate pre-signed upload URL
      const { signedUploadUrl, objectKey, publicFileUrl } = await generateUploadUrl({
        orderId: params.orderId,
        fileExtension,
        fileSize,
      });
      
      // Step 2: Upload file directly to R2
      await uploadToR2({
        signedUploadUrl,
        file: params.file,
        onProgress: params.onProgress,
      });
      
      // Step 3: Save upload metadata to database (preserve original filename)
      const { imageId } = await saveUploadMetadata({
        objectKey,
        orderId: params.orderId,
        slotIndex: params.slotIndex,
        role: params.role,
        imageId: params.imageId,
        parentImageId: params.parentImageId,
        fileName: params.file.name,
      });
      
      // Success! Return result
      return {
        success: true,
        imageId,
        publicFileUrl,
      };
      
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Don't retry on authentication errors (401) - these won't be fixed by retrying
      if (lastError.message.includes('Authentication failed')) {
        throw lastError;
      }
      
      // Don't retry on authorization errors (403 from API, not R2) - user doesn't have permission
      if (lastError.message.includes('Order does not belong to user')) {
        throw lastError;
      }
      
      // Don't retry on validation errors (400) - invalid input won't be fixed by retrying
      if (lastError.message.includes('File exceeds maximum') || 
          lastError.message.includes('Invalid file size') ||
          lastError.message.includes('Missing required fields')) {
        throw lastError;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries - 1) {
        throw new Error(`Upload failed after ${maxRetries} attempts: ${lastError.message}`);
      }
      
      // Calculate exponential backoff delay: 0ms, 1000ms, 2000ms
      const delayMs = attempt * 1000;
      
      // Log retry attempt (for debugging)
      console.warn(`[uploadFileToR2] Attempt ${attempt + 1} failed, retrying in ${delayMs}ms...`, {
        orderId: params.orderId,
        fileName: params.file.name,
        error: lastError.message,
      });
      
      // Wait before retrying (skip delay on first retry)
      if (delayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
      
      // Loop continues to next attempt, which will request a new signed URL
    }
  }
  
  // This should never be reached due to the throw in the loop, but TypeScript needs it
  throw new Error(`Upload failed: ${lastError?.message || 'Unknown error'}`);
}
